import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { graphql } from "gatsby";
import { FormattedMessage } from 'react-intl';

import Layout from '../components/Layout';
import LocalizedLink from '../components/LocalizedLink';
import HeadMeta from '../components/HeadMeta';

const ARTICLES_PER_PAGE = 6;

const CATEGORIES = {
  fr: { all: 'Tous', telecom: 'Télécom', ia: 'IA', eurocodes: 'Eurocodes', bim: 'BIM', actualites: 'Actualités' },
  en: { all: 'All', telecom: 'Telecom', ia: 'AI', eurocodes: 'Eurocodes', bim: 'BIM', actualites: 'News' },
};

const metaData = {
  en: {
    title: `Blog — TriDyme`,
    pageTitle: "Blog",
    description: "News, tutorials and insights on engineering.",
    searchPlaceholder: "Search articles...",
    noResults: "No articles found",
    showing: "Showing",
    of: "of",
    articles: "articles"
  },
  fr: {
    title: `Blog — TriDyme`,
    pageTitle: "Blog",
    description: "Actualités, tutoriels et retours d'expérience sur l'ingénierie et l'innovation dans la Construction et l'Immobilier.",
    searchPlaceholder: "Rechercher un article...",
    noResults: "Aucun article trouvé",
    showing: "Affichage de",
    of: "sur",
    articles: "articles"
  }
};

const Blog = ({ pageContext: { locale }, data }) => {
  // Only the articles written in the page language (FR on /blog, EN on /en/blog)
  const articles = useMemo(() => data.allMarkdownRemark.edges.filter(({ node }) => (
    (node.frontmatter.language || 'fr').toLowerCase().startsWith(locale)
  )), [data, locale]);
  const categoryLabels = CATEGORIES[locale] || CATEGORIES.fr;
  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter articles based on search query
  const filteredArticles = useMemo(() => {
    const inCategory = category === 'all'
      ? articles
      : articles.filter(({ node }) => node.frontmatter.category === category);
    if (!searchQuery.trim()) return inCategory;
    const query = searchQuery.toLowerCase();
    return inCategory.filter(({ node }) => {
      const title = node.frontmatter.title?.toLowerCase() || '';
      const description = node.frontmatter.description?.toLowerCase() || '';
      const excerpt = node.excerpt?.toLowerCase() || '';
      return title.includes(query) || description.includes(query) || excerpt.includes(query);
    });
  }, [articles, category, searchQuery]);

  // Get featured article (first one) and other articles
  const featuredArticle = filteredArticles[0]?.node;
  const otherArticles = filteredArticles.slice(1);

  // Pagination
  const totalPages = Math.ceil(otherArticles.length / ARTICLES_PER_PAGE);
  const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
  const paginatedArticles = otherArticles.slice(startIndex, startIndex + ARTICLES_PER_PAGE);

  // Reset to page 1 when search changes
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  return (
    <Layout locale={locale}>
      <HeadMeta locale={locale} metaData={metaData} />

      <div className="blog-page">
        {/* Page Header */}
        <section className="blog-page-header">
          <h1>{metaData[locale].pageTitle}</h1>
          <p>{metaData[locale].description}</p>

          {/* Search Bar */}
          <div className="blog-search">
            <div className="search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={metaData[locale].searchPlaceholder}
                value={searchQuery}
                onChange={handleSearch}
                className="search-input"
              />
              {searchQuery && (
                <button className="search-clear" onClick={() => { setSearchQuery(''); setCurrentPage(1); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Categories */}
          <div className="blog-categories" role="group" aria-label={locale === 'en' ? 'Categories' : 'Catégories'}>
            {Object.keys(categoryLabels).map((key) => (
              <button
                key={key}
                type="button"
                className={`blog-category ${category === key ? 'active' : ''}`}
                aria-pressed={category === key}
                onClick={() => { setCategory(key); setCurrentPage(1); }}
              >
                {categoryLabels[key]}
              </button>
            ))}
          </div>
        </section>

        {/* Blog Section */}
        <section className="blog-section">
          <div className="blog-container">
            {/* No results message */}
            {filteredArticles.length === 0 && (
              <div className="no-results">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <p>{metaData[locale].noResults}</p>
              </div>
            )}

            {/* Featured Article */}
            {featuredArticle && filteredArticles.length > 0 && (
              <article className="featured-article">
                <div className="featured-image">
                  {featuredArticle.frontmatter.featuredImage ? (
                    <img
                      src={featuredArticle.frontmatter.featuredImage.publicURL}
                      alt={featuredArticle.frontmatter.title}
                    />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                    </svg>
                  )}
                </div>
                <div className="featured-content">
                  <span className="article-tag">{categoryLabels[featuredArticle.frontmatter.category] || 'Article'}</span>
                  <h2>
                    <LocalizedLink to={featuredArticle.fields.slug}>
                      {featuredArticle.frontmatter.title}
                    </LocalizedLink>
                  </h2>
                  <p className="article-excerpt">
                    {featuredArticle.frontmatter.description || featuredArticle.excerpt}
                  </p>
                  <div className="article-meta">
                    <div className="article-author">
                      <span className="author-avatar">
                        {featuredArticle.frontmatter.author
                          ? featuredArticle.frontmatter.author.split(' ').map(n => n[0]).join('')
                          : 'TD'}
                      </span>
                      <span>{featuredArticle.frontmatter.author || 'TriDyme'}</span>
                    </div>
                    <span>•</span>
                    <span>{featuredArticle.frontmatter.date}</span>
                    <span>•</span>
                    <span>5 min</span>
                  </div>
                </div>
              </article>
            )}

            {/* Articles Grid */}
            {paginatedArticles.length > 0 && (
              <div className="articles-grid">
                {paginatedArticles.map(({ node }) => (
                  <article className="article-card" key={node.id}>
                    <LocalizedLink to={node.fields.slug} className="article-card-link">
                      <div className="article-card-image">
                        {node.frontmatter.featuredImage ? (
                          <img
                            src={node.frontmatter.featuredImage.publicURL}
                            alt={node.frontmatter.title}
                          />
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                          </svg>
                        )}
                      </div>
                      <div className="article-card-content">
                        <span className="article-tag">{categoryLabels[node.frontmatter.category] || 'Article'}</span>
                        <h3>{node.frontmatter.title}</h3>
                        <p>{node.frontmatter.description || node.excerpt}</p>
                        <div className="article-meta">
                          <span>{node.frontmatter.date}</span>
                          <span>•</span>
                          <span>5 min</span>
                        </div>
                      </div>
                    </LocalizedLink>
                  </article>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="blog-pagination">
                <div className="pagination-info">
                  {metaData[locale].showing} {startIndex + 1}-{Math.min(startIndex + ARTICLES_PER_PAGE, otherArticles.length)} {metaData[locale].of} {otherArticles.length} {metaData[locale].articles}
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`pagination-btn pagination-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    className="pagination-btn"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
    </Layout>
  );
};

Blog.propTypes = {
  pageContext: PropTypes.shape({
    locale: PropTypes.string.isRequired
  }).isRequired
};

export default Blog;

export const query = graphql`
  query {
    allMarkdownRemark(
      filter: { fileAbsolutePath: {regex : "\/blog/"} },
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      totalCount
      edges {
        node {
          id
          frontmatter {
            title
            date(formatString: "DD MMMM YYYY", locale: "fr")
            language
            author
            featuredImage {
              publicURL
            }
            description
            status
            category
          }
          fields {
            slug
          }
          excerpt
        }
      }
    }
  }
`;
